/**
 * SPCB Checkout Auth Module
 * Handles login modal, registration enhancement, and birthday datepicker
 *
 * v1.8.0: Extracted from conditional-fields.js
 * v1.7.0: Original implementation
 *
 * Functions: 20
 * - Login: injectLoginBanner, createLoginModal, showLoginModal, closeLoginModal,
 *          handleLoginEscape, trapFocusInModal, handleLoginSubmit, initLoginBanner
 * - Registration: createRegistrationModal, showRegistrationModal, closeRegistrationModal,
 *                 handleRegistrationEscape, cancelRegistration, confirmRegistration,
 *                 updateRegistrationButton, enhanceRegistrationArea, initRegistrationEnhancement
 * - Birthday: hideBirthdayField, initBirthdayDatepicker
 */

(function() {
    'use strict';

    // Debug mode
    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB Auth]', ...args);

    // ============================================
    // LOGIN BANNER & MODAL
    // ============================================
    // For non-logged-in users: banner "¿Ya tienes cuenta?" with AJAX login modal

    /**
     * Inject login banner above checkout contact fields
     * Only shows for non-logged-in users
     */
    function injectLoginBanner() {
        // Don't show if user is logged in
        if (document.body.classList.contains('logged-in')) {
            log('Login banner: User is logged in, skipping');
            return;
        }

        // Don't duplicate
        if (document.querySelector('.spcb-login-banner')) {
            return;
        }

        // Find the "Información de contacto" section - try multiple selectors
        // WooCommerce Blocks structure varies by version
        let contactBlock = document.querySelector('.wc-block-checkout__contact-fields');

        if (!contactBlock) {
            contactBlock = document.querySelector('.wp-block-woocommerce-checkout-contact-information-block');
        }

        if (!contactBlock) {
            // Try finding by heading text
            const headings = document.querySelectorAll('.wc-block-components-checkout-step__title, h2, h3');
            for (const heading of headings) {
                if (heading.textContent.toLowerCase().includes('contacto') ||
                    heading.textContent.toLowerCase().includes('contact')) {
                    contactBlock = heading.closest('.wc-block-components-checkout-step') ||
                                   heading.closest('.wp-block-woocommerce-checkout-contact-information-block') ||
                                   heading.parentElement;
                    break;
                }
            }
        }

        if (!contactBlock) {
            log('Login banner: Contact block not found, trying checkout form');
            // Last resort: insert at beginning of checkout form
            contactBlock = document.querySelector('.wc-block-checkout__form, .wc-block-checkout');
        }

        if (!contactBlock) {
            log('Login banner: No suitable container found');
            return;
        }

        log('Login banner: Found container', contactBlock.className);

        // Create banner
        const banner = document.createElement('div');
        banner.className = 'spcb-login-banner';
        banner.innerHTML = `
            <div class="spcb-login-banner__content">
                <span class="spcb-login-banner__text">¿Ya tienes cuenta?</span>
                <button type="button" class="spcb-login-banner__button">Iniciar Sesión</button>
            </div>
            <div class="spcb-login-banner__subtitle">Si no, completa los siguientes datos</div>
        `;

        // Insert before the contact block
        contactBlock.parentNode.insertBefore(banner, contactBlock);

        // Event listener for login button
        const loginButton = banner.querySelector('.spcb-login-banner__button');
        loginButton.addEventListener('click', showLoginModal);

        log('Login banner injected');
    }

    /**
     * Show AJAX login modal
     */
    let loginModalCreated = false;

    /**
     * Create login modal (same structure as other modals)
     */
    function createLoginModal() {
        if (loginModalCreated) return;

        const config = window.spcbConfig || {};

        const modalHTML = `
            <div id="spcb-login-modal" class="spcb-modal" style="display:none;" role="dialog" aria-modal="true" aria-labelledby="spcb-login-title">
                <div class="spcb-modal-overlay"></div>
                <div class="spcb-modal-content">
                    <div class="spcb-modal-header">
                        <h3 id="spcb-login-title">Iniciar Sesión</h3>
                        <button type="button" class="spcb-modal-close" aria-label="Cerrar">&times;</button>
                    </div>
                    <div class="spcb-modal-body">
                        <div class="spcb-login-benefits">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                            <span>¡Bienvenido! Inicia sesión para ver tus pedidos, direcciones guardadas y comprar más rápido.</span>
                        </div>
                        <hr class="spcb-login-divider">
                        <form class="spcb-login-form" id="spcb-login-form">
                            <div class="spcb-login-error" role="alert" aria-live="polite" style="display:none;"></div>
                            <div class="spcb-field spcb-field-with-icon">
                                <label for="spcb-login-username">Email o usuario</label>
                                <div class="spcb-input-wrapper">
                                    <svg class="spcb-field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                    </svg>
                                    <input type="text" id="spcb-login-username" name="username"
                                           required autocomplete="username"
                                           aria-required="true">
                                </div>
                            </div>
                            <div class="spcb-field spcb-field-with-icon">
                                <label for="spcb-login-password">Contraseña</label>
                                <div class="spcb-input-wrapper">
                                    <svg class="spcb-field-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                    </svg>
                                    <input type="password" id="spcb-login-password" name="password"
                                           required autocomplete="current-password"
                                           aria-required="true">
                                </div>
                            </div>
                            <div class="spcb-field spcb-remember-field">
                                <label class="spcb-checkbox-label">
                                    <input type="checkbox" name="remember" value="1">
                                    <span class="spcb-checkbox-custom"></span>
                                    <span>Recordarme</span>
                                </label>
                            </div>
                            <button type="submit" class="spcb-login-submit">Entrar</button>
                            <a href="${config.lostPasswordUrl || '/mi-cuenta/lost-password/'}"
                               class="spcb-lost-password" target="_blank">
                                ¿Olvidaste tu contraseña?
                            </a>
                            <div class="spcb-login-register">
                                ¿No tienes cuenta? <a href="#" class="spcb-register-link">Regístrate</a>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);

        const modal = document.getElementById('spcb-login-modal');
        if (modal) {
            // Event listeners (NO cerrar con click en overlay - solo con X)
            modal.querySelector('.spcb-modal-close').addEventListener('click', closeLoginModal);
            modal.querySelector('#spcb-login-form').addEventListener('submit', handleLoginSubmit);

            // v1.16.7: Link para ir a registro
            modal.querySelector('.spcb-register-link')?.addEventListener('click', (e) => {
                e.preventDefault();
                closeLoginModal();

                // Esperar a que el modal se cierre completamente
                setTimeout(() => {
                    const registerBtn = document.querySelector('.spcb-registration-button');
                    log('Register button found:', !!registerBtn);
                    if (registerBtn) {
                        registerBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Esperar a que termine el scroll
                        setTimeout(() => {
                            registerBtn.classList.remove('spcb-shake'); // Reset primero
                            void registerBtn.offsetWidth; // Force reflow
                            registerBtn.classList.add('spcb-shake');
                            log('Shake class added');
                            setTimeout(() => registerBtn.classList.remove('spcb-shake'), 600);
                        }, 600);
                    }
                }, 300);
            });
        }

        loginModalCreated = true;
        log('Login modal created');
    }

    function showLoginModal() {
        createLoginModal();
        const modal = document.getElementById('spcb-login-modal');
        if (!modal) return;

        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';

        // NO autofocus - interfiere con password managers

        // ESC to close
        document.addEventListener('keydown', handleLoginEscape);

        log('Login modal opened');
    }

    /**
     * Close login modal
     */
    function closeLoginModal() {
        const modal = document.getElementById('spcb-login-modal');
        if (modal) {
            modal.style.display = 'none';
            document.body.style.overflow = '';
        }
        document.removeEventListener('keydown', handleLoginEscape);
        log('Login modal closed');
    }

    function handleLoginEscape(e) {
        if (e.key === 'Escape') {
            closeLoginModal();
        }
    }

    /**
     * Trap focus inside modal (accessibility)
     */
    function trapFocusInModal(modal) {
        const focusableElements = modal.querySelectorAll(
            'input, button, a[href], [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        modal.addEventListener('keydown', (e) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        });
    }

    /**
     * Handle login form submission via AJAX
     * v1.10.23: Simplified - WooCommerce handles cart merge natively
     */
    async function handleLoginSubmit(e) {
        e.preventDefault();

        const form = e.target;
        const submitBtn = form.querySelector('.spcb-login-submit');
        const errorDiv = form.querySelector('.spcb-login-error');
        const config = window.spcbConfig || {};

        const username = form.querySelector('#spcb-login-username').value;
        const password = form.querySelector('#spcb-login-password').value;
        const remember = form.querySelector('[name="remember"]').checked;

        // Loading state (accessible)
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.textContent = 'Entrando...';
        errorDiv.style.display = 'none';
        errorDiv.textContent = '';

        try {
            const response = await fetch(config.ajaxUrl || '/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'spcb_ajax_login',
                    username: username,
                    password: password,
                    remember: remember ? '1' : '0',
                    nonce: config.loginNonce || ''
                })
            });

            const data = await response.json();

            if (data.success) {
                // v1.10.23: Just reload - WooCommerce handles cart merge natively
                // We'll detect the merge after reload in checkAndShowMergeModal()
                window.location.reload();
            } else {
                // Show error (announces automatically via role="alert")
                errorDiv.textContent = data.data?.message || 'Error al iniciar sesión';
                errorDiv.style.display = 'block';
                submitBtn.disabled = false;
                submitBtn.setAttribute('aria-busy', 'false');
                submitBtn.textContent = 'Entrar';

                // Focus on error for screen readers
                errorDiv.setAttribute('tabindex', '-1');
                errorDiv.focus();
            }
        } catch (error) {
            errorDiv.textContent = 'Error de conexión. Intenta de nuevo.';
            errorDiv.style.display = 'block';
            submitBtn.disabled = false;
            submitBtn.setAttribute('aria-busy', 'false');
            submitBtn.textContent = 'Entrar';
        }
    }

    // ============================================
    // REGISTRATION ENHANCEMENT (v1.7.0)
    // ============================================
    // Visual banner around "Create account" checkbox with benefits list

    /**
     * Enhance registration area with visual banner
     * Wraps the native checkbox with attractive design
     */
    let registrationModalCreated = false;
    let wcCreateAccountCheckbox = null;

    /**
     * Create registration modal (button + modal pattern)
     */
    function createRegistrationModal() {
        if (registrationModalCreated) return;

        const config = window.spcbConfig || {};
        const regConfig = config.registration || {};

        // Configurable benefits (default if not set)
        const benefits = regConfig.benefits || [
            'Seguimiento de pedidos en tiempo real',
            'Checkout más rápido en futuras compras',
            'Historial completo de compras'
        ];
        const benefitsHTML = benefits.map(b => `<li>✓ ${b}</li>`).join('');

        // Configurable birthday message and toggle
        const birthdayEnabled = regConfig.birthdayEnabled !== false; // Default to true
        const birthdayMessage = regConfig.birthdayMessage || 'Te enviaremos un regalo especial en tu cumpleaños';
        const birthdayHintHTML = birthdayEnabled ? `
                            <div class="spcb-birthday-hint">
                                <span>📅</span>
                                <span>${birthdayMessage}</span>
                            </div>` : '';

        // Configurable description
        const description = regConfig.description || 'Tu cuenta se creará al finalizar la compra. Recibirás un correo para configurar tu clave.';

        const modalHTML = `
            <div id="spcb-registration-modal" class="spcb-modal" style="display:none;" role="dialog" aria-modal="true" aria-labelledby="spcb-registration-title">
                <div class="spcb-modal-overlay"></div>
                <div class="spcb-modal-content">
                    <div class="spcb-modal-header">
                        <h3 id="spcb-registration-title">Crear una cuenta</h3>
                        <button type="button" class="spcb-modal-close" aria-label="Cerrar">&times;</button>
                    </div>
                    <div class="spcb-modal-body">
                        <p class="spcb-registration-description">${description}</p>
                        <ul class="spcb-registration-benefits">
                            ${benefitsHTML}
                        </ul>
                        <div class="spcb-registration-checkbox-area">
                            <label class="spcb-checkbox-label">
                                <input type="checkbox" id="spcb-create-account-mirror" name="create-account-mirror">
                                <span class="spcb-checkbox-custom"></span>
                                <span>Crear mi cuenta</span>
                            </label>
                        </div>
                        <div class="spcb-birthday-area" style="display:none;">
                            ${birthdayHintHTML}
                            <div class="spcb-field">
                                <label for="spcb-birthday-input">Fecha de cumpleaños (opcional)</label>
                                <input type="date" id="spcb-birthday-input" name="birthday"
                                       min="1900-01-01" max="${new Date().toISOString().split('T')[0]}">
                            </div>
                        </div>
                    </div>
                    <div class="spcb-modal-footer">
                        <button type="button" class="spcb-modal-button-secondary spcb-registration-cancel">No gracias</button>
                        <button type="button" class="spcb-modal-button spcb-registration-confirm">Crear cuenta</button>
                    </div>
                </div>
            </div>
        `;

        document.body.insertAdjacentHTML('beforeend', modalHTML);

        const modal = document.getElementById('spcb-registration-modal');
        if (modal) {
            // NO cerrar con click en overlay - solo con X o botones
            modal.querySelector('.spcb-modal-close').addEventListener('click', closeRegistrationModal);
            modal.querySelector('.spcb-registration-confirm').addEventListener('click', confirmRegistration);
            modal.querySelector('.spcb-registration-cancel').addEventListener('click', cancelRegistration);

            // Toggle birthday area when checkbox changes
            const mirrorCheckbox = modal.querySelector('#spcb-create-account-mirror');
            const birthdayArea = modal.querySelector('.spcb-birthday-area');
            mirrorCheckbox.addEventListener('change', () => {
                birthdayArea.style.display = mirrorCheckbox.checked ? 'block' : 'none';
            });
        }

        registrationModalCreated = true;
        log('Registration modal created');
    }

    function showRegistrationModal() {
        createRegistrationModal();
        const modal = document.getElementById('spcb-registration-modal');
        if (!modal) return;

        // Auto-check the checkbox when opening (user clicked "create account" button)
        const mirrorCheckbox = modal.querySelector('#spcb-create-account-mirror');
        const birthdayArea = modal.querySelector('.spcb-birthday-area');
        if (mirrorCheckbox) {
            mirrorCheckbox.checked = true;
            birthdayArea.style.display = 'block';
        }

        // Pre-fill birthday if WC field has value
        const wcBirthdayField = document.querySelector('[data-spcb-field="birthday"], input[id*="spcb-birthday"]');
        const birthdayInput = modal.querySelector('#spcb-birthday-input');
        if (wcBirthdayField && birthdayInput && wcBirthdayField.value) {
            // Convert DD/MM/YYYY to YYYY-MM-DD for native date input
            const parts = wcBirthdayField.value.split('/');
            if (parts.length === 3) {
                birthdayInput.value = `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
        }

        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleRegistrationEscape);

        log('Registration modal opened');
    }

    function closeRegistrationModal() {
        const modal = document.getElementById('spcb-registration-modal');
        if (modal) {
            modal.style.display = 'none';
            document.body.style.overflow = '';
        }
        document.removeEventListener('keydown', handleRegistrationEscape);
        log('Registration modal closed');
    }

    function handleRegistrationEscape(e) {
        if (e.key === 'Escape') {
            closeRegistrationModal();
        }
    }

    /**
     * Cancel registration - uncheck WC checkbox and close modal
     */
    function cancelRegistration() {
        // Make sure WC checkbox is unchecked
        if (wcCreateAccountCheckbox && wcCreateAccountCheckbox.checked) {
            wcCreateAccountCheckbox.click(); // Trigger proper React update
        }
        updateRegistrationButton();
        closeRegistrationModal();
        log('Registration cancelled');
    }

    function confirmRegistration() {
        const modal = document.getElementById('spcb-registration-modal');
        if (!modal) return;

        const mirrorCheckbox = modal.querySelector('#spcb-create-account-mirror');
        const birthdayInput = modal.querySelector('#spcb-birthday-input');

        // Sync checkbox state to WC
        if (wcCreateAccountCheckbox && mirrorCheckbox) {
            if (wcCreateAccountCheckbox.checked !== mirrorCheckbox.checked) {
                wcCreateAccountCheckbox.click(); // Trigger proper React update
            }
        }

        // v1.10.21: Password field is handled by PHP filter (forceAutoGeneratePasswordOnCheckout)
        // which forces WC to auto-generate password on checkout. No JS hack needed.

        // Sync birthday to WC field
        const wcBirthdayField = document.querySelector('[data-spcb-field="birthday"], input[id*="spcb-birthday"]');
        if (wcBirthdayField && birthdayInput && birthdayInput.value) {
            // Convert YYYY-MM-DD to DD/MM/YYYY
            const parts = birthdayInput.value.split('-');
            if (parts.length === 3) {
                const formattedDate = `${parts[2]}/${parts[1]}/${parts[0]}`;
                wcBirthdayField.value = formattedDate;
                // v1.15.2: Guard to prevent dispatch during checkout processing
                if (!window.spcbCheckoutLock?.isProcessing?.()) {
                    wcBirthdayField.dispatchEvent(new Event('input', { bubbles: true }));
                    wcBirthdayField.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        }

        // Update button text
        updateRegistrationButton();

        closeRegistrationModal();
        log('Registration confirmed');
    }

    function updateRegistrationButton() {
        const button = document.querySelector('.spcb-registration-button');
        if (!button) return;

        const isCreatingAccount = wcCreateAccountCheckbox?.checked;
        if (isCreatingAccount) {
            button.classList.add('spcb-registration-active');
            button.innerHTML = `
                <span class="spcb-registration-icon">✓</span>
                <span class="spcb-registration-title">Cuenta activada - Editar</span>
            `;
        } else {
            button.classList.remove('spcb-registration-active');
            button.innerHTML = `
                <span class="spcb-registration-icon">🎁</span>
                <span class="spcb-registration-title">¡Crea tu cuenta y obtén beneficios!</span>
            `;
        }
    }

    function enhanceRegistrationArea() {
        // Only for non-logged-in users
        if (document.body.classList.contains('logged-in')) {
            return;
        }

        // Don't duplicate
        if (document.querySelector('.spcb-registration-button')) {
            return;
        }

        // Find the create account checkbox - try multiple selectors
        wcCreateAccountCheckbox = document.querySelector(
            '.wc-block-checkout__create-account input[type="checkbox"], ' +
            '.wc-block-components-checkbox input[name="create-account"], ' +
            'input[id*="create-account"]'
        );

        if (!wcCreateAccountCheckbox) {
            return;
        }

        // Find the parent container of the checkbox
        const checkboxContainer = wcCreateAccountCheckbox.closest('.wc-block-checkout__create-account') ||
                                  wcCreateAccountCheckbox.closest('.wc-block-components-checkbox') ||
                                  wcCreateAccountCheckbox.closest('.wc-block-components-form-row');

        if (!checkboxContainer) {
            return;
        }

        // Hide the original WC checkbox area
        checkboxContainer.style.display = 'none';

        // Also hide the WC birthday field (we have our own in the modal)
        hideBirthdayField();

        // Create button that opens modal
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'spcb-registration-button';
        button.innerHTML = `
            <span class="spcb-registration-icon">🎁</span>
            <span class="spcb-registration-title">¡Crea tu cuenta y obtén beneficios!</span>
        `;
        button.addEventListener('click', showRegistrationModal);

        // Insert button before the hidden checkbox
        checkboxContainer.parentNode.insertBefore(button, checkboxContainer);

        // Update button state based on WC checkbox
        wcCreateAccountCheckbox.addEventListener('change', updateRegistrationButton);
        updateRegistrationButton();

        log('Registration button injected');
    }

    /**
     * Initialize login banner with retry logic for WC Blocks DOM changes
     */
    function initLoginBanner() {
        log('initLoginBanner: Starting, logged-in class:', document.body.classList.contains('logged-in'));

        // Only for non-logged-in users
        if (document.body.classList.contains('logged-in')) {
            log('initLoginBanner: User is logged in, skipping');
            return;
        }

        log('initLoginBanner: User is NOT logged in, setting up injection');

        // Initial injection attempts
        setTimeout(injectLoginBanner, 100);
        setTimeout(injectLoginBanner, 500);
        setTimeout(injectLoginBanner, 1000);

        // MutationObserver to re-inject if WC Blocks rebuilds DOM
        const loginBannerObserver = new MutationObserver(() => {
            if (!document.querySelector('.spcb-login-banner') && !document.body.classList.contains('logged-in')) {
                injectLoginBanner();
            }
        });

        if (document.body) {
            loginBannerObserver.observe(document.body, { childList: true, subtree: true });
        }

        log('Login banner initialization started');
    }

    /**
     * Initialize registration enhancement with retry logic
     */
    function initRegistrationEnhancement() {
        log('initRegistrationEnhancement: Starting, logged-in class:', document.body.classList.contains('logged-in'));

        // Only for non-logged-in users
        if (document.body.classList.contains('logged-in')) {
            log('initRegistrationEnhancement: User is logged in, hiding birthday field');
            // Hide birthday field for logged-in users
            hideBirthdayField();
            return;
        }

        log('initRegistrationEnhancement: User is NOT logged in, setting up enhancement');

        // Initial injection attempts
        setTimeout(enhanceRegistrationArea, 100);
        setTimeout(enhanceRegistrationArea, 500);
        setTimeout(enhanceRegistrationArea, 1000);
        setTimeout(enhanceRegistrationArea, 2000);

        // Observer to re-apply if WC rebuilds
        const registrationObserver = new MutationObserver(() => {
            if (!document.querySelector('.spcb-registration-button') && !document.body.classList.contains('logged-in')) {
                enhanceRegistrationArea();
            }
        });

        if (document.body) {
            registrationObserver.observe(document.body, { childList: true, subtree: true });
        }

        log('Registration enhancement initialization started');
    }

    // ============================================
    // BIRTHDAY DATE PICKER (v1.7.0)
    // ============================================

    let birthdayPickerInitialized = false;

    /**
     * Hide the birthday field
     * Called when user is logged in or when "create account" is unchecked
     */
    function hideBirthdayField() {
        const selectors = [
            '[data-spcb-field="birthday"]',
            'input[id*="spcb-birthday"]',
            'input[id*="birthday"][placeholder*="DD"]'
        ];

        for (const selector of selectors) {
            const field = document.querySelector(selector);
            if (field) {
                const wrapper = field.closest('.wc-block-components-text-input') ||
                               field.closest('.wc-block-components-form-row') ||
                               field.parentElement;
                if (wrapper) {
                    wrapper.style.display = 'none';
                    log('Birthday field hidden');
                    return;
                }
            }
        }

        // Retry after delay if field not found yet
        setTimeout(() => {
            for (const selector of selectors) {
                const field = document.querySelector(selector);
                if (field) {
                    const wrapper = field.closest('.wc-block-components-text-input') ||
                                   field.closest('.wc-block-components-form-row') ||
                                   field.parentElement;
                    if (wrapper) {
                        wrapper.style.display = 'none';
                        log('Birthday field hidden (delayed)');
                    }
                }
            }
        }, 1000);
    }

    /**
     * Initialize flatpickr date picker on birthday field
     */
    function initBirthdayDatepicker() {
        if (birthdayPickerInitialized) return;

        // Check if flatpickr is available
        if (typeof flatpickr === 'undefined') {
            log('Birthday datepicker: flatpickr not loaded');
            return;
        }

        // Find birthday field
        const birthdayInput = document.querySelector(
            'input[data-spcb-field="birthday"], ' +
            'input[id*="spcb-birthday"], ' +
            'input[id*="birthday"][placeholder*="DD"]'
        );

        if (!birthdayInput) {
            log('Birthday datepicker: input not found');
            return;
        }

        // Don't initialize twice
        if (birthdayInput._flatpickr) {
            return;
        }

        // Initialize flatpickr
        flatpickr(birthdayInput, {
            locale: typeof flatpickr.l10ns.es !== 'undefined' ? 'es' : 'default',
            dateFormat: 'd/m/Y',
            altInput: true,
            altFormat: 'j F Y',
            allowInput: true,
            maxDate: 'today',
            minDate: '1900-01-01',
            defaultDate: null,
            disableMobile: false,
            monthSelectorType: 'dropdown',
            yearSelectorType: 'dropdown',
            // Callback when date changes
            onChange: function(selectedDates, dateStr, instance) {
                // Trigger change event for WC Blocks validation
                // v1.15.2: Guard to prevent dispatch during checkout processing
                if (!window.spcbCheckoutLock?.isProcessing?.()) {
                    birthdayInput.dispatchEvent(new Event('change', { bubbles: true }));
                    birthdayInput.dispatchEvent(new Event('input', { bubbles: true }));
                }
            },
            onOpen: function() {
                log('Birthday datepicker opened');
            }
        });

        birthdayPickerInitialized = true;
        log('Birthday datepicker initialized');
    }

    // ============================================
    // EXPOSE API FOR OTHER MODULES
    // ============================================

    window.spcbAuth = {
        initLoginBanner,
        initRegistrationEnhancement,
        initBirthdayDatepicker,
        hideBirthdayField
    };

    log('SPCB Auth module loaded');

})();
